import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, MapPin, Sparkles, Clock, TrendingUp, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog';
import { getMultiIndexSuggestions } from '@/lib/search/algolia/multiIndexSearch';
import { useQuery } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { SectionedSearchResults } from './SectionedSearchResults';

interface EnhancedSearchPopupProps {
  children: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const EnhancedSearchPopup: React.FC<EnhancedSearchPopupProps> = ({
  children,
  open,
  onOpenChange
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  // Load recent searches from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('lightmarket-recent-searches');
    if (saved) {
      try {
        setRecentSearches(JSON.parse(saved));
      } catch (e) {
        console.warn('Failed to parse recent searches');
      }
    }
  }, []);

  // Save search to history
  const saveToHistory = (query: string) => {
    if (!query.trim()) return;
    
    const updated = [query.trim(), ...recentSearches.filter(s => s !== query.trim())].slice(0, 5);
    setRecentSearches(updated);
    localStorage.setItem('lightmarket-recent-searches', JSON.stringify(updated));
  };

  // Get live suggestions from Algolia (multi-index search)
  const { data: suggestions, isLoading } = useQuery({
    queryKey: ['algolia-multi-search', searchQuery],
    queryFn: async () => {
      if (!searchQuery.trim() || searchQuery.length < 2) {
        return {
          sequences: [],
          displays: [],
          sequenceCount: 0,
          displayCount: 0,
        };
      }
      return await getMultiIndexSuggestions(searchQuery);
    },
    enabled: searchQuery.length >= 2,
    staleTime: 10000,
  });

  // Transform API results to structured display and sequence results
  const { displayResults, sequenceResults } = useMemo(() => {
    const displays: Array<{ id: string; title: string; subtitle?: string; url: string }> = [];
    const sequences: Array<{ id: string; title: string; subtitle?: string; url: string; imageUrl?: string }> = [];

    // Add display suggestions
    if (suggestions?.displays) {
      suggestions.displays.forEach((hit: any) => {
        displays.push({
          id: `display-${hit.objectID}`,
          title: hit.display_name || hit.name,
          subtitle: `${hit.city || ''}${hit.city && hit.state ? ', ' : ''}${hit.state || ''}`,
          url: `/displays/${hit.objectID}`
        });
      });
    }

    // Add sequence suggestions
    if (suggestions?.sequences) {
      suggestions.sequences.forEach((hit: any) => {
        sequences.push({
          id: `sequence-${hit.objectID}`,
          title: hit.title || hit.name,
          subtitle: `${hit.artist_name || 'Unknown Artist'}`,
          url: `/sequence/${hit.objectID}`,
          imageUrl: hit.image_url || hit.thumbnail_url || ''
        });
      });
    }

    return { displayResults: displays, sequenceResults: sequences };
  }, [suggestions]);

  // Handle keyboard navigation
  const totalResults = displayResults.length + sequenceResults.length;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev =>
        prev < Math.min(6, totalResults) - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => prev > -1 ? prev - 1 : -1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (searchQuery.trim()) {
        handleSmartSearch();
      }
    } else if (e.key === 'Escape') {
      onOpenChange?.(false);
    }
  };

  // Smart search routing - route to type with more results
  const handleSmartSearch = () => {
    if (!searchQuery.trim()) return;

    saveToHistory(searchQuery);
    onOpenChange?.(false);

    const displayCount = suggestions?.displayCount || 0;
    const sequenceCount = suggestions?.sequenceCount || 0;

    // Route to the type with more results, tie-breaker goes to sequences
    if (displayCount > sequenceCount) {
      navigate(`/displays?q=${encodeURIComponent(searchQuery.trim())}`);
    } else {
      navigate(`/sequences?q=${encodeURIComponent(searchQuery.trim())}`);
    }

    setSearchQuery('');
    setSelectedIndex(-1);
  };

  // Handle recent search click - use smart routing
  const handleRecentSearch = (query: string) => {
    setSearchQuery(query);
    // Delay to allow state update, then trigger smart search
    setTimeout(() => {
      handleSmartSearch();
    }, 0);
  };

  // Auto-focus input when opened
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  // Reset selections when query changes
  useEffect(() => {
    setSelectedIndex(-1);
  }, [searchQuery]);

  const hasQuery = searchQuery.length >= 2;
  const hasAnyResults = displayResults.length > 0 || sequenceResults.length > 0;
  const hasRecentSearches = recentSearches.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="sm:max-w-2xl pb-0 gap-0 max-h-[80vh] overflow-hidden">
        <div className="flex flex-col">
          {/* Search Header */}
          <div className="flex items-center p-4 border-b">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={18} />
              <Input 
                ref={inputRef}
                className="pl-10 pr-4 border-0 focus-visible:ring-0 text-lg"
                placeholder="Search displays, sequences, or genres..." 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleKeyDown}
              />
              {searchQuery && (
                <Button 
                  variant="ghost" 
                  size="sm"
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0"
                  onClick={() => setSearchQuery('')}
                >
                  <X size={14} />
                </Button>
              )}
            </div>
            {hasQuery && (
              <Button
                onClick={handleSmartSearch}
                className="ml-3"
                size="sm"
              >
                Search
              </Button>
            )}
          </div>

          {/* Search Results */}
          <div className="flex-1 overflow-y-auto max-h-[50vh]">
            {hasQuery ? (
              <div className="p-4">
                <SectionedSearchResults
                  displayResults={displayResults}
                  sequenceResults={sequenceResults}
                  searchQuery={searchQuery}
                  isLoading={isLoading}
                  onResultClick={() => {
                    onOpenChange?.(false);
                    setSearchQuery('');
                  }}
                  selectedIndex={selectedIndex}
                />
              </div>
            ) : (
              <div className="p-4">
                {/* Recent Searches */}
                {hasRecentSearches && (
                  <div className="mb-6">
                    <div className="flex items-center space-x-2 mb-3">
                      <Clock size={16} className="text-muted-foreground" />
                      <span className="text-sm font-medium text-muted-foreground">Recent Searches</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {recentSearches.map((query, index) => (
                        <Button
                          key={index}
                          variant="outline"
                          size="sm"
                          onClick={() => handleRecentSearch(query)}
                          className="text-xs"
                        >
                          {query}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Quick Links */}
                <div>
                  <div className="flex items-center space-x-2 mb-3">
                    <TrendingUp size={16} className="text-muted-foreground" />
                    <span className="text-sm font-medium text-muted-foreground">Popular Categories</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        onOpenChange?.(false);
                        navigate('/sequences?holiday=Christmas');
                      }}
                      className="justify-start"
                    >
                      <Sparkles size={14} className="mr-2 text-blue-500" />
                      Christmas Sequences
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        onOpenChange?.(false);
                        navigate('/displays?holiday=christmas');
                      }}
                      className="justify-start"
                    >
                      <MapPin size={14} className="mr-2 text-green-500" />
                      Christmas Displays
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        onOpenChange?.(false);
                        navigate('/sequences?holiday=Halloween');
                      }}
                      className="justify-start"
                    >
                      <Sparkles size={14} className="mr-2 text-blue-500" />
                      Halloween Sequences
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        onOpenChange?.(false);
                        navigate('/displays?holiday=halloween');
                      }}
                      className="justify-start"
                    >
                      <MapPin size={14} className="mr-2 text-green-500" />
                      Halloween Displays
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};